<template>
  <Alert
    v-if="newAppVersionBanner.isVisible"
    :severity="newAppVersionBanner.severity"
    icon="sparkles"
    title="Update Available"
    :message="`A new version (${newAppVersionBanner.latestVersion}) of the app is available. Refresh the page or click on update below.`"
    action-button
    action-button-text="Update"
    :show-dismiss-button="false"
    :show-close-button="false"
    class="mb-6 bg-info"
    @action="refreshPage()"
  />
  <div
    id="navbar"
    :class="{ 'app-expanded-menu': mainMenuState == 'lockedOpen' }"
  >
    <div
      id="navbar-content"
      class="flex flex-row items-center"
    >
      <ul class="flex flex-1 sm:flex-none xl:hidden cursor-pointer">
        <li class="nav-item">
          <Button
            icon="bars"
            plain
            severity="secondary"
            @click="openMobileMenu()"
          />
        </li>
      </ul>
      <!-- Favourites -->
      <!-- TODO: determine if we want to keep favourites feature? -->
      <!-- <ul class="hidden sm:flex sm:flex-1" id="navbar-favs">
          <li v-for="item in starredItems" :key="item.name" class="nav-item">
              <router-link :to="item.route">
                  <Icon :type="item.icon" />
              </router-link>
          </li>

          <li class="nav-item">
            <Dropdown text="Dropdown">
              <template v-slot:triggerContent>
                  <Icon type="star" class="text-yellow-500" />
              </template>
              <DropdownItem v-for="item in favsToolbar" :key="item.name" :route="item.route">
                <div class="flex">
                  <Icon :type="item.icon" style="margin-right: 10px" />
                  <span class="flex-1">{{ $t("terms." + item.localeTerm, 2) }}</span>
                  <Icon type="star" :class="{ 'text-yellow-500': item.isFav }" style="margin-left: 10px"
                    @click.prevent="toggleFav(item)" />
                </div>
              </DropdownItem>
          </Dropdown>
        </li>
      </ul> -->
      <ul class="sm:flex sm:flex-1 ml-2">
        <li class="nav-item">
          <div
            class="cursor-pointer flex items-center justify-start"
            @click="toggleSearchPanel('open')"
          >
            <Icon
              type="magnifying-glass"
              class="text-lg"
            />
            <div class="hidden sm:flex items-center">
              <span class="pl-2 text-quaternary text-lg leading-[1]">{{ $t('ui.common.search') }}</span>
              <span class="text-xs py-0.5 px-1.5 text-secondary border border-secondary rounded-md ml-2">
                <span v-if="isMac">⌘</span>
                <span v-else>Ctrl</span>
                K
              </span>
            </div>
          </div>
        </li>
      </ul>
      <!-- Right -->
      <ul class="flex items-center gap-2">
        <!-- Active Call Center Selector -->
        <li
          v-if="$can('make_calls') && userPresence"
          class="nav-item hidden sm:block"
        >
          <div class="text-[0.9rem] flex justify-between border border-gray-300 dark:border-darkmode-3 rounded">
            <Dropdown>
              <template #triggerContent>
                <div class="cursor-pointer py-1 px-2 flex gap-2 items-center justify-center">
                  <Icon type="headset" />
                  <div v-if="selectedCallCenter">{{ selectedCallCenterName }}</div>
                  <div
                    v-else
                    class="text-quaternary"
                  >
                    Select Call Center
                  </div>
                  <Icon type="chevron-down" />
                </div>
              </template>
              <DropdownItem
                v-for="center in callCenters"
                :key="center.id"
                @item-clicked="changeCallCenter(center.id)"
              >
                {{ center.name }}
              </DropdownItem>
              <DropdownItem @item-clicked="changeCallCenter(null)">None</DropdownItem>
            </Dropdown>
          </div>
        </li>

        <li class="nav-item">
          <Dropdown>
            <template #triggerContent>
              <Button
                plain
                severity="secondary"
                size="lg"
                icon="language"
              />
            </template>
            <DropdownItem @item-clicked="changeLocale('en-GB')">English (UK)</DropdownItem>
            <DropdownItem @item-clicked="changeLocale('en-US')">English (US)</DropdownItem>
            <DropdownItem @item-clicked="changeLocale('es')">Spanish</DropdownItem>
            <DropdownItem @item-clicked="changeLocale('fr')">French</DropdownItem>
          </Dropdown>

          <Button
            plain
            severity="secondary"
            size="lg"
            icon="location-dot"
            router-link="/access"
          />

          <div class="hidden">
            <Dropdown>
              <template #triggerContent>
                <Button
                  plain
                  severity="secondary"
                  size="lg"
                  icon="bell"
                />
              </template>
              <div style="min-width: 400px">
                <div class="px-5 pt-3 pb-1">
                  <h4 class="font-medium">Notifications</h4>
                </div>
                <DropdownSeparator />

                <Chip class="ml-5">Coming Soon!</Chip>

                <!-- <NotificationsList /> -->

                <DropdownSeparator />
                <div class="px-5 pt-1 pb-3">
                  <Button
                    label="View All"
                    block
                    plain
                    router-link="/"
                  />
                </div>
              </div>
            </Dropdown>
          </div>

          <Dropdown
            text="Dropdown"
            :close-on-click-within="false"
            :padding="false"
          >
            <template #triggerContent>
              <Button
                plain
                severity="secondary"
                size="lg"
                :icon="selectedThemeObj.icon ?? 'moon'"
              />
            </template>
            <div class="px-3 py-2">
              <RadioTabbed
                v-model="selectedTheme"
                :options="systemThemeOptions"
                @option-change="darkmodeChange"
              />
            </div>
          </Dropdown>
        </li>
        <li class="nav-item cursor-pointer !text-md flex">
          <Dropdown text="Dropdown">
            <template #triggerContent>
              <Avatar
                size="sm"
                :url="userStore.details.profilePicUrl"
                :user-presence-user-id="userStore.details.id"
                :text="userStore.details.initials"
                :status-indicator-icon="userPresence.icon"
                :status-indicator-color-class="userPresence.color"
              />
            </template>
            <DropdownItem :route="`/settings/users/${userStore.details.id}`">
              <AvatarLabelGroup
                :label="userStore.details.name"
                :description="userStore.details.email"
                :avatar-url="userStore.details.profilePicUrl"
                :avatar-text="userStore.details.initials"
              />
            </DropdownItem>

            <DropdownSeparator />

            <!-- <DropdownItem route="/me/profile">
              <Icon type="user" />
              <span class="w-full">{{ $t('ui.entities.profile.label') }}</span>
            </DropdownItem>-->
            <DropdownItem :route="`/settings/users/${userStore.details.id}`">
              <Icon type="cog" />
              <span class="w-full">{{ $t('ui.entities.setting', 2) }}</span>
            </DropdownItem>

            <DropdownSeparator />

            <!-- INFO: These use the a href link as canny requires the 'data-canny-link' attribute to be on the anchor tag -->
            <!-- <a data-canny-link href="https://retain-automotive.canny.io/changelog">
              <DropdownItem v-if="userStore.details.userType == 1">
                <Icon type="sparkles" />
                <span class="w-full">{{ $t('ui.support.whatsNew') }}</span>
              </DropdownItem>
            </a> -->

            <a
              data-canny-link
              href="https://retain-automotive.canny.io/feature-requests"
            >
              <DropdownItem v-if="userStore.details.userType == 1">
                <Icon type="lightbulb-on" />
                <span class="w-full">{{ $t('ui.support.featureRequests') }}</span>
              </DropdownItem>
            </a>

            <a
              data-canny-link
              href="https://retain-automotive.canny.io/bugs-issues"
            >
              <DropdownItem v-if="userStore.details.userType == 1">
                <Icon type="bug" />
                <span class="w-full">{{ $t('ui.support.reportIssue') }}</span>
              </DropdownItem>
            </a>

            <DropdownItem link="mailto:support@retain.group">
              <Icon type="ticket" />
              <span class="w-full">{{ $t('ui.support.label') }}</span>
            </DropdownItem>

            <DropdownSeparator />

            <DropdownItem @item-clicked="logout">
              <Icon type="arrow-right-from-bracket" />
              <span class="w-full">{{ $t('ui.common.signOut') }}</span>
            </DropdownItem>
          </Dropdown>
        </li>
      </ul>
    </div>
  </div>
  <GlobalSearch
    :is-open="searchIsOpen"
    @close-search="closeSearch"
  />
</template>

<script setup>
import { computed, ref, onMounted, inject } from 'vue'

import Icon from '@/components/icon/Icon.vue'
import { state, mobileMenuIsOpen } from '@/components/main-menu/state'
import Avatar from '@/components/avatar/Avatar.vue'
import AvatarLabelGroup from '@/components/avatar/AvatarLabelGroup.vue'
import Button from '@/components/button/Button.vue'
import Chip from '@/components/chip/Chip.vue'
import Dropdown from '@/components/dropdown/Dropdown.vue'
import DropdownItem from '@/components/dropdown/DropdownItem.vue'
import DropdownSeparator from '@/components/dropdown/DropdownSeparator.vue'
import RadioTabbed from '@/components/forms/RadioTabbed.vue'
import Alert from '@/components/alert/Alert.vue'

import GlobalSearch from '@/components/search/GlobalSearch.vue'

import { useUserStore } from '@/stores/UserStore.ts'
import { useSocketStore } from '@/stores/SocketStore.ts'

const socketStore = useSocketStore()

import { useEventListener } from '@vueuse/core'

import initializeCanny from '@/plugins/canny.js'

defineProps({
  darkMode: Boolean
})
const searchIsOpen = ref(false)
const localeIso = ref(localStorage.getItem('locale'))
const mainMenuState = state
const newAppVersionBanner = ref({
  isVisible: false,
  version: null,
  severity: null
})

function refreshPage() {
  location.reload()
}

const emitter = inject('emitter')

onMounted(() => {
  // console.log('Listening for version mismatch event')
  // Listen for the version mismatch event
  emitter.on('version-mismatch', ({ currentVersion, latestVersion }) => {
    // console.log('Show Banner ', currentVersion, latestVersion)
    newAppVersionBanner.value.isVisible = true
    newAppVersionBanner.value.currentVersion = currentVersion
    newAppVersionBanner.value.latestVersion = latestVersion
    newAppVersionBanner.value.severity = 'info' // or 'warning' or 'danger' based on your needs
  })
})

const userStore = useUserStore() // PINIA

onMounted(() => {
  // Call the initializeCanny function with the user data
  initializeCanny(userStore.details)
})

const favsToolbarDefaults = [
  {
    name: 'inbox',
    icon: 'inbox',
    route: '/inbox',
    isFav: true,
    localeTerm: 'inbox'
  },
  {
    name: 'flow',
    icon: 'wind',
    route: '/flow',
    isFav: false,
    localeTerm: 'flow'
  },
  {
    name: 'appointments',
    icon: 'calendar-days',
    route: '/appointments/calendar',
    isFav: true,
    localeTerm: 'appointment'
  },
  {
    name: 'availability',
    icon: 'calendar-check',
    route: '/appointments/availability',
    isFav: false,
    localeTerm: 'availability'
  },
  {
    name: 'tasks',
    icon: 'check-double',
    route: '/tasks',
    isFav: true,
    localeTerm: 'task'
  }
]

if (!localStorage.getItem('favsToolbar')) {
  const init = favsToolbarDefaults.filter(item => item.isFav)
  const initDefaults = []

  init.forEach(item => {
    initDefaults.push(item.name)
  })
  localStorage.setItem('favsToolbar', JSON.stringify(initDefaults))
}

// const favsToolbar = ref(favsToolbarDefaults)
// const favs = ref(JSON.parse(localStorage.getItem('favsToolbar')) || [])

// //Loop through each faved item and update favsToolbar
// favsToolbarDefaults.forEach(item => {
//   // console.log(item.name)

//   if (favs.value.includes(item.name)) {
//     item.isFav = true
//   } else {
//     item.isFav = false
//   }
// })

// const starredItems = computed(() => {
//   return favsToolbar.value.filter(item => item.isFav)
// })

// function toggleFav(item) {
//   // console.log('fav toggled')

//   if (item.isFav) {
//     // If already a fav, remove from array
//     favs.value = favs.value.filter(e => e !== item.name)
//   } else {
//     // Else, add to array
//     favs.value.push(item.name)
//   }

//   item.isFav = !item.isFav

//   //Update local storage
//   localStorage.setItem('favsToolbar', JSON.stringify(favs.value))
// }

const selectedTheme = computed(() => userStore.userSettings.darkmode)
const systemThemeOptions = [
  {
    label: 'Light',
    value: 'light',
    icon: 'sun-bright'
  },
  {
    label: 'Dark',
    value: 'dark',
    icon: 'moon'
  },
  {
    label: 'Auto',
    value: 'auto',
    icon: 'eclipse'
  }
]

const selectedThemeObj = computed(() => {
  // Find the object which matched the selected theme value
  // Check if the settings have been set. Else, should show default icon as to not throw undefined error
  let themeInStore

  if (userStore.userSettings.length > 0) {
    themeInStore = systemThemeOptions.find(theme => theme.value == userStore.stateSettings.darkmode)
  } else {
    themeInStore = systemThemeOptions[0]
  }

  return themeInStore
})

function darkmodeChange(option) {
  userStore.updateUserSettings([
    {
      name: 'darkmode',
      value: option
    }
  ])
}

function openMobileMenu() {
  mobileMenuIsOpen.value = true
}

function changeLocale(iso) {
  if (localeIso.value != iso) {
    localeIso.value = iso
    localStorage.setItem('locale', iso)

    location.reload()
  }
}

function logout() {
  userStore.signOut()
}

// Call Center
const callCenters = computed(() => userStore.callCenters ?? [])
const selectedCallCenter = computed(() => userStore.selectedCallCenter)

const selectedCallCenterName = computed(() => {
  const callCenter = callCenters.value?.find(callCenter => callCenter.id == selectedCallCenter.value)
  return callCenter?.name
})

function changeCallCenter(id) {
  userStore.selectCallCenter(id)
}

const userPresence = computed(() => {
  return socketStore.getUserPresence(userStore.details.id)
})

function toggleSearchPanel(action = null) {
  if (action != null) {
    if (action == 'open') {
      searchIsOpen.value = true
    } else {
      searchIsOpen.value = false
    }
  } else {
    searchIsOpen.value = !searchIsOpen.value
  }
}

const isMac = /Mac|iPod|iPhone|iPad/.test(window.navigator.userAgent)

// Listen for Cmd+k for searching
const onKeydown = event => {
  const modifier = isMac ? 'metaKey' : 'ctrlKey'
  const cmdK = event[modifier] && event.key.toLowerCase() === 'k'

  if (cmdK) {
    event.preventDefault()
    toggleSearchPanel()
  }
}
useEventListener(document, 'keydown', onKeydown)

function closeSearch() {
  searchIsOpen.value = false
}

// let currentAppVersion = packageInfo.version
// socket.on('new-fe-version-available', ({ version, severity }) => {
//   console.log('new-fe-version-available: ' + version)
//   if (currentAppVersion !== version) {
//     newAppVersionBanner.value.isVisible = true
//     newAppVersionBanner.value.version = version
//     newAppVersionBanner.value.severity = severity
//   }
// })
</script>

<style>
#navbar {
  box-sizing: border-box;
  inline-size: 100%;
  margin-inline: auto;
  max-inline-size: 1440px;
}

.dark #navbar {
  background: none;
}

#navbar::after {
  position: absolute;
  z-index: -1;
  -webkit-backdrop-filter: blur(15px);
  backdrop-filter: blur(15px);
  background-repeat: repeat;
  block-size: calc(64px + 1.5rem);
  content: '';
  inset-block-start: -1.02rem;
  inset-inline-end: 0;
  inset-inline-start: 0;
  -webkit-mask: linear-gradient(black, black 18%, transparent 100%);
  mask: linear-gradient(black, black 18%, transparent 100%);
}

#navbar-content {
  /* box-shadow: var(--shadowCard); */
  padding: var(--s-xxs) var(--s-xs) var(--s-xxs) var(--s-xs);
  border-radius: var(--rounded-xl);
  border: 1px solid;
  border-color: var(--border-secondary);
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
  height: 100%;
  background-color: var(--bg-primary_alt);
}

.dark #navbar-content {
  border-color: transparent;
}
</style>
